import React, { useState, useCallback } from "react";
import { useNavStep } from "../lib/NavHistory";

const APIS = [
  { id: "pubmed", label: "PubMed", color: "#2563eb", icon: "🔬" },
  { id: "crossref", label: "CrossRef", color: "#7c3aed", icon: "📚" },
  { id: "europepmc", label: "Europe PMC", color: "#059669", icon: "🇪🇺" },
  { id: "semanticscholar", label: "Semantic Scholar", color: "#dc2626", icon: "🎓" },
];

const DATE_RANGES = [
  { value: 3, label: "3 months" },
  { value: 6, label: "6 months" },
  { value: 12, label: "1 year" },
  { value: 24, label: "2 years" },
  { value: 60, label: "5 years" },
];

// Evidence-tier colors (mirrors routes/_evidence_ranking.js tiers; CRA can't
// import from routes/, so the palette is duplicated here by tier CODE).
const TIER_COLORS = {
  SYSTEMATIC_REVIEW:    { fg: "#15803d", bg: "#dcfce7" },
  DB_RCT:               { fg: "#0369a1", bg: "#e0f2fe" },
  OL_RCT:               { fg: "#2563eb", bg: "#eff6ff" },
  QUASI_EXPERIMENTAL:   { fg: "#4f46e5", bg: "#eef2ff" },
  PROSPECTIVE_COHORT:   { fg: "#b45309", bg: "#fef3c7" },
  RETROSPECTIVE_COHORT: { fg: "#c2410c", bg: "#ffedd5" },
  CROSS_SECTIONAL:      { fg: "#6b7280", bg: "#f3f4f6" },
  CASE_REPORT:          { fg: "#be123c", bg: "#ffe4e6" },
  GUIDELINE:            { fg: "#7c3aed", bg: "#f3e8ff" },
  UNCLEAR:              { fg: "#64748b", bg: "#f1f5f9" },
};

export default function ManualSearchPanel({ isOpen, onClose }) {
  // #65 Phase C1: register this overlay as a single Back step so the global
  // Back (button / keyboard / Android hardware) closes it before navigating.
  // Unconditional at the top (rules-of-hooks); generic label, never PHI.
  useNavStep({ active: isOpen, label: 'Manual search', onBack: () => onClose && onClose() });
  const [query, setQuery] = useState("");
  const [selectedApis, setSelectedApis] = useState(["pubmed", "crossref", "europepmc", "semanticscholar"]);
  const [dateRange, setDateRange] = useState(12);
  const [maxResults, setMaxResults] = useState(30);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [selectedArticles, setSelectedArticles] = useState(new Set());
  const [saveMessage, setSaveMessage] = useState(null);
  const [expandedAbstract, setExpandedAbstract] = useState(null);

  const toggleApi = useCallback((apiId) => {
    setSelectedApis(prev => {
      if (prev.includes(apiId)) {
        if (prev.length <= 1) return prev; // Must keep at least one
        return prev.filter(a => a !== apiId);
      }
      return [...prev, apiId];
    });
  }, []);

  const handleSearch = useCallback(async () => {
    if (!query.trim() || selectedApis.length === 0) return;
    setLoading(true);
    setError(null);
    setResults(null);
    setSelectedArticles(new Set());
    setSaveMessage(null);

    try {
      const res = await window.zignagenixAPI.manualSearch(query.trim(), selectedApis, maxResults, dateRange);
      if (res?.error) {
        setError(res.message || "Search failed");
      } else {
        setResults(res);
      }
    } catch (err) {
      setError(err.message || "Search failed");
    } finally {
      setLoading(false);
    }
  }, [query, selectedApis, maxResults, dateRange]);

  const toggleArticle = useCallback((idx) => {
    setSelectedArticles(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    if (!results?.articles) return;
    if (selectedArticles.size === results.articles.length) {
      setSelectedArticles(new Set());
    } else {
      setSelectedArticles(new Set(results.articles.map((_, i) => i)));
    }
  }, [results, selectedArticles]);

  const handleSave = useCallback(async () => {
    if (selectedArticles.size === 0 || !results?.articles) return;
    const toSave = [...selectedArticles].map(i => results.articles[i]);
    try {
      const res = await window.zignagenixAPI.bulkSaveArticles(toSave, null);
      if (res?.error) {
        setError(res.message);
      } else {
        setSaveMessage(`Saved ${res.saved} article(s). ${res.duplicates} duplicate(s) skipped.`);
        setTimeout(() => setSaveMessage(null), 5000);
        setSelectedArticles(new Set());
      }
    } catch (err) {
      setError(err.message || "Save failed");
    }
  }, [selectedArticles, results]);

  if (!isOpen) return null;

  const panelStyle = {
    position: "fixed", top: 0, right: 0, bottom: 0, width: 520,
    background: "#fff", boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
    zIndex: 10000, display: "flex", flexDirection: "column",
    fontFamily: "'Inter', -apple-system, sans-serif",
    transition: "transform 0.3s ease",
  };

  const headerStyle = {
    padding: "14px 18px", background: "linear-gradient(135deg, #1e3a5f, #2c5282)",
    color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center",
    flexShrink: 0,
  };

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20 }}>🔍</span>
          <span style={{ fontSize: 16, fontWeight: 700 }}>Research Search</span>
          <span style={{
            fontSize: 10, background: "rgba(255,255,255,0.2)", padding: "2px 8px",
            borderRadius: 10, fontWeight: 600,
          }}>Manual</span>
        </div>
        <button onClick={onClose} style={{
          background: "rgba(255,255,255,0.15)", border: "none", color: "#fff",
          width: 28, height: 28, borderRadius: "50%", cursor: "pointer",
          fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center",
        }}>✕</button>
      </div>

      {/* Search Controls */}
      <div style={{ padding: "14px 18px", borderBottom: "1px solid #e5e7eb", flexShrink: 0 }}>
        {/* Query Input */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            placeholder="Search terms (e.g. CRISPR gene therapy, cardiac biomarkers)..."
            style={{
              flex: 1, padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 8,
              fontSize: 13, outline: "none",
            }}
          />
          <button
            onClick={handleSearch}
            disabled={loading || !query.trim() || selectedApis.length === 0}
            style={{
              padding: "9px 16px", background: loading ? "#94a3b8" : "#2563eb",
              color: "#fff", border: "none", borderRadius: 8, cursor: loading ? "wait" : "pointer",
              fontWeight: 600, fontSize: 13, whiteSpace: "nowrap",
            }}
          >
            {loading ? "Searching..." : "Search"}
          </button>
        </div>

        {/* API Selector Pills */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6, fontWeight: 600 }}>
            Select APIs:
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {APIS.map(api => {
              const active = selectedApis.includes(api.id);
              return (
                <button key={api.id} onClick={() => toggleApi(api.id)} style={{
                  padding: "4px 10px", borderRadius: 14, fontSize: 11, fontWeight: 600,
                  border: `1.5px solid ${active ? api.color : "#d1d5db"}`,
                  background: active ? `${api.color}15` : "#fff",
                  color: active ? api.color : "#6b7280",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                  transition: "all 0.15s",
                }}>
                  <span>{api.icon}</span> {api.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Date Range & Max Results */}
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Date range
            </label>
            <select value={dateRange} onChange={e => setDateRange(Number(e.target.value))} style={{
              width: "100%", padding: "6px 8px", border: "1px solid #d1d5db",
              borderRadius: 6, fontSize: 12, background: "#fff",
            }}>
              {DATE_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Max results
            </label>
            <select value={maxResults} onChange={e => setMaxResults(Number(e.target.value))} style={{
              width: "100%", padding: "6px 8px", border: "1px solid #d1d5db",
              borderRadius: 6, fontSize: 12, background: "#fff",
            }}>
              {[10, 20, 30, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Error / Save Message */}
      {error && (
        <div style={{
          padding: "8px 18px", background: "#fef2f2", color: "#dc2626", fontSize: 12,
          borderBottom: "1px solid #fecaca", fontWeight: 500, flexShrink: 0,
        }}>
          {error}
          <button onClick={() => setError(null)} style={{
            float: "right", background: "none", border: "none", color: "#dc2626",
            cursor: "pointer", fontWeight: 700,
          }}>✕</button>
        </div>
      )}
      {saveMessage && (
        <div style={{
          padding: "8px 18px", background: "#dcfce7", color: "#166534", fontSize: 12,
          borderBottom: "1px solid #86efac", fontWeight: 600, flexShrink: 0,
        }}>
          {saveMessage}
        </div>
      )}

      {/* Results Area */}
      <div style={{ flex: 1, overflowY: "auto", padding: 0 }}>
        {loading && (
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>🔍</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Searching {selectedApis.length} API{selectedApis.length > 1 ? "s" : ""}...</div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>This may take a few seconds</div>
          </div>
        )}

        {!loading && !results && !error && (
          <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📑</div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Enter a search query to find research articles</div>
            <div style={{ fontSize: 11, marginTop: 8, lineHeight: 1.6 }}>
              Search across PubMed, CrossRef, Europe PMC,<br />and Semantic Scholar simultaneously
            </div>
          </div>
        )}

        {results && !loading && (
          <>
            {/* Result Summary Bar */}
            <div style={{
              padding: "10px 18px", background: "#f8fafc", borderBottom: "1px solid #e5e7eb",
              display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0,
            }}>
              <div style={{ fontSize: 12, color: "#374151" }}>
                <strong>{results.articles?.length || 0}</strong> articles found
                {results.totalCount > (results.articles?.length || 0) && (
                  <span style={{ color: "#9ca3af" }}> (of ~{results.totalCount.toLocaleString()} total)</span>
                )}
                {results.errors?.length > 0 && (
                  <span style={{ color: "#f59e0b", marginLeft: 8, fontSize: 11 }}>
                    ⚠ {results.errors.length} API error{results.errors.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {results.articles?.length > 0 && (
                  <>
                    <button onClick={selectAll} style={{
                      padding: "4px 10px", fontSize: 11, border: "1px solid #d1d5db",
                      borderRadius: 6, background: "#fff", cursor: "pointer", fontWeight: 500,
                    }}>
                      {selectedArticles.size === results.articles.length ? "Deselect all" : "Select all"}
                    </button>
                    {selectedArticles.size > 0 && (
                      <button onClick={handleSave} style={{
                        padding: "4px 10px", fontSize: 11, border: "none",
                        borderRadius: 6, background: "#059669", color: "#fff",
                        cursor: "pointer", fontWeight: 600,
                      }}>
                        💾 Save {selectedArticles.size} selected
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Article List */}
            {(results.articles || []).map((article, idx) => {
              const isSelected = selectedArticles.has(idx);
              const isExpanded = expandedAbstract === idx;
              const apiMeta = APIS.find(a => a.id === article.sourceApi);
              // Evidence tier (server-ranked). Insert a divider whenever the tier
              // changes from the previous result, so evidence levels never
              // intermix without a clear label (directive item 4).
              const ev = article.evidence || null;
              const prevEv = idx > 0 ? (results.articles[idx - 1].evidence || null) : null;
              const tierChanged = ev && (!prevEv || prevEv.rank !== ev.rank);
              const tc = ev ? (TIER_COLORS[ev.code] || TIER_COLORS.UNCLEAR) : null;

              return (
                <React.Fragment key={idx}>
                {tierChanged && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 18px 4px", background: "#fafbfc",
                    borderTop: idx > 0 ? "2px solid #e5e7eb" : "none",
                  }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: tc.fg, background: tc.bg,
                      padding: "2px 8px", borderRadius: 8,
                    }}>
                      {Number.isInteger(ev.rank) ? `${ev.rank}. ` : ""}{ev.label}
                    </span>
                    <span style={{ fontSize: 10, color: "#9ca3af" }}>{ev.strength}</span>
                  </div>
                )}
                <div style={{
                  padding: "12px 18px", borderBottom: "1px solid #f3f4f6",
                  background: isSelected ? "#eff6ff" : "#fff",
                  cursor: "pointer", transition: "background 0.1s",
                }} onClick={() => toggleArticle(idx)}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    {/* Checkbox */}
                    <div style={{
                      width: 18, height: 18, borderRadius: 4, border: `2px solid ${isSelected ? "#2563eb" : "#d1d5db"}`,
                      background: isSelected ? "#2563eb" : "#fff", flexShrink: 0, marginTop: 2,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontSize: 11, fontWeight: 700,
                    }}>
                      {isSelected && "✓"}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Title */}
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1e3a5f", lineHeight: 1.4, marginBottom: 4 }}>
                        {article.title || "Untitled"}
                      </div>

                      {/* Authors & Journal */}
                      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4, lineHeight: 1.3 }}>
                        {article.authors && (
                          <span>{article.authors.length > 80 ? article.authors.slice(0, 80) + "..." : article.authors}</span>
                        )}
                        {article.journal && (
                          <span style={{ fontStyle: "italic" }}> — {article.journal}</span>
                        )}
                      </div>

                      {/* Metadata chips */}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                        {ev && tc && (
                          <span style={{
                            fontSize: 10, padding: "1px 7px", borderRadius: 8,
                            background: tc.bg, color: tc.fg, fontWeight: 700,
                            border: `1px solid ${tc.fg}30`,
                          }} title={`Evidence rank ${ev.rank} — ${ev.strength}${ev.derived === 'ai' ? ' (AI-classified)' : ''}`}>
                            {ev.label}
                          </span>
                        )}
                        {apiMeta && (
                          <span style={{
                            fontSize: 10, padding: "1px 6px", borderRadius: 8,
                            background: `${apiMeta.color}15`, color: apiMeta.color,
                            fontWeight: 600, border: `1px solid ${apiMeta.color}30`,
                          }}>
                            {apiMeta.icon} {apiMeta.label}
                          </span>
                        )}
                        {article.publicationDate && (
                          <span style={{
                            fontSize: 10, padding: "1px 6px", borderRadius: 8,
                            background: "#f3f4f6", color: "#6b7280", fontWeight: 500,
                          }}>
                            📅 {article.publicationDate}
                          </span>
                        )}
                        {article.citationCount > 0 && (
                          <span style={{
                            fontSize: 10, padding: "1px 6px", borderRadius: 8,
                            background: "#fef3c7", color: "#92400e", fontWeight: 600,
                          }}>
                            📖 {article.citationCount} citations
                          </span>
                        )}
                        {article.isOpenAccess && (
                          <span style={{
                            fontSize: 10, padding: "1px 6px", borderRadius: 8,
                            background: "#dcfce7", color: "#166534", fontWeight: 600,
                          }}>
                            🔓 Open Access
                          </span>
                        )}
                        {article.doi && (
                          <span style={{
                            fontSize: 10, padding: "1px 6px", borderRadius: 8,
                            background: "#f0f9ff", color: "#1e40af", fontWeight: 500,
                          }}>
                            DOI
                          </span>
                        )}
                      </div>

                      {/* Abstract toggle */}
                      {article.abstract && (
                        <div style={{ marginTop: 6 }}>
                          <button onClick={e => { e.stopPropagation(); setExpandedAbstract(isExpanded ? null : idx); }}
                            style={{
                              fontSize: 11, color: "#2563eb", background: "none",
                              border: "none", cursor: "pointer", padding: 0, fontWeight: 500,
                            }}
                          >
                            {isExpanded ? "▾ Hide abstract" : "▸ Show abstract"}
                          </button>
                          {isExpanded && (
                            <div style={{
                              fontSize: 11, color: "#4b5563", lineHeight: 1.5,
                              marginTop: 6, padding: "8px 10px", background: "#f9fafb",
                              borderRadius: 6, maxHeight: 200, overflowY: "auto",
                            }}>
                              {article.abstract}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                </React.Fragment>
              );
            })}

            {results.articles?.length === 0 && (
              <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🤷</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>No articles found for this query</div>
                <div style={{ fontSize: 11, marginTop: 4 }}>Try broadening your search terms or date range</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: "8px 18px", borderTop: "1px solid #e5e7eb", background: "#f8fafc",
        fontSize: 10, color: "#9ca3af", textAlign: "center", flexShrink: 0,
      }}>
        Searches PubMed, CrossRef, Europe PMC & Semantic Scholar • Results can be saved to Research Database
      </div>
    </div>
  );
}
